import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { AlertTriangle, ChevronDown, Circle, Search, X } from "lucide-react";
import i18next from "i18next";
import { useTranslation } from "react-i18next";
import LevelContext from "../../../utils/UserLevel";

const getNodeNumber = (node) =>
  String(node?.account_no || node?.accountNumber || node?.id || "");

const getNodeName = (node, isArabic) => {
  const name = isArabic
    ? node?.name_ar || node?.accountNameAR
    : node?.name_en || node?.accountNameEN;

  return String(name ?? "");
};

const normalizeAccountType = (value) => {
  const normalized = String(value || "").toLowerCase();

  if (normalized === "sub") return "secondary";
  if (normalized === "secondary") return "secondary";
  if (normalized === "key") return "secondary";
  if (normalized === "primary") return "primary";
  if (normalized === "main") return "primary";

  return "";
};

const resolveNodeType = (node) => {
  const explicitType = normalizeAccountType(
    node?.account_type || node?.accountType
  );

  if (explicitType) return explicitType;

  return Array.isArray(node?.childs) && node.childs.length > 0
    ? "primary"
    : "secondary";
};

const normalizeNodes = (nodes, isArabic, parentKey = "root", level = 0) => {
  return (Array.isArray(nodes) ? nodes : []).map((node, index) => {
    const number = getNodeNumber(node);
    const name = getNodeName(node, isArabic);

    const key = `${parentKey}-${node?.id ?? number ?? index}-${index}`;

    const children = normalizeNodes(node?.childs || [], isArabic, key, level + 1);

    return {
      key,
      id: String(node?.id ?? number ?? index),
      accountNumber: number,
      accountName: name,
      raw: { ...node, level: level + 1 },
      children,
      level: level + 1,
    };
  });
};

const flattenNodes = (nodes, list = []) => {
  nodes.forEach((node) => {
    list.push(node);

    if (node.children.length > 0) {
      flattenNodes(node.children, list);
    }
  });

  return list;
};

const TreeRow = ({
  node,
  level,
  isExpanded,
  isSelected,
  canSelect,
  onToggle,
  onSelect,
  isArabic,
  selectLabel,
}) => {
  const hasChildren = node.children.length > 0;

  const displayLabel = isArabic
    ? `${node.accountName} - ${node.accountNumber}`
    : `${node.accountNumber} - ${node.accountName}`;

  return (
    <div>
      <div
        className="group flex items-center justify-between gap-2 rounded-md px-2 py-1.5 hover:bg-slate-100"
        style={{ paddingInlineStart: `${level * 20 + 6}px` }}
      >
        <button
          type="button"
          className="flex items-center gap-2 min-w-0 text-start"
          onClick={() => {
            if (hasChildren) {
              onToggle(node.key);
              return;
            }

            if (canSelect) {
              onSelect(node);
            }
          }}
        >
          <span className="w-5 h-5 flex items-center justify-center text-[#261759]">
            {hasChildren ? (
              <ChevronDown
                size={14}
                className={`transition-transform duration-200 ${
                  !isExpanded ? "rotate-0" : "-rotate-90"
                }`}
              />
            ) : (
              <Circle size={8} />
            )}
          </span>

          <span className="truncate text-[15px] font-[700] text-[#261759]">
            {displayLabel}
          </span>
        </button>

        <button
          type="button"
          className={`shrink-0 px-2 py-1 text-xs font-[700] rounded-md border ${
            isSelected
              ? "bg-[#261759] text-white border-[#261759]"
              : "bg-white text-[#261759] border-[#261759] hover:bg-[#e9f0f8]"
          } disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white`}
          disabled={!canSelect}
          onClick={() => onSelect(node)}
        >
          {selectLabel}
        </button>
      </div>
    </div>
  );
};

const SearchResultRow = ({
  node,
  isSelected,
  canSelect,
  onSelect,
  isArabic,
  selectLabel,
}) => {
  const displayLabel = isArabic
    ? `${node.accountName} - ${node.accountNumber}`
    : `${node.accountNumber} - ${node.accountName}`;

  return (
    <div className="flex items-center justify-between gap-2 rounded-md px-2 py-2 border border-slate-200 bg-white">
      <span className="truncate text-[14px] font-[700] text-[#2d6297]">
        {displayLabel}
      </span>

      <button
        type="button"
        className={`shrink-0 px-2 py-1 text-xs font-[700] rounded-md border ${
          isSelected
            ? "bg-[#2d6297] text-white border-[#2d6297]"
            : "bg-white text-[#2d6297] border-[#2d6297] hover:bg-[#e9f0f8]"
        } disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white`}
        disabled={!canSelect}
        onClick={() => onSelect(node)}
      >
        {selectLabel}
      </button>
    </div>
  );
};

const TreePopup = ({
  isOpen,
  onClose,
  data = [],
  onSelect,
  selectedValue = "",
  title,
  allowedAccountTypes = [],
  blockedAccountNumbers = [],
  isAccountCenter = false,
  maxNodeLevel = null,
}) => {
  const [t] = useTranslation("global");

  const [searchTerm, setSearchTerm] = useState("");
  const [expanded, setExpanded] = useState({});

  const { chart_account_level_limit , cost_center_level_limit } = useContext(
    LevelContext
  ) || {};
  const nodeLevelLimit = useMemo(
    () =>
      maxNodeLevel !== null
        ? maxNodeLevel
        : isAccountCenter
          ? cost_center_level_limit
          : chart_account_level_limit,
    [maxNodeLevel, isAccountCenter, cost_center_level_limit, chart_account_level_limit]
  );

  const isArabic = i18next.dir(i18next.language) === "rtl";

  const selectLabel = t("common.select");
  const entityLabel = isAccountCenter
    ? t("accounts.costCenterAccount")
    : t("accounts.account");
  const levelNotice =
    nodeLevelLimit === null || nodeLevelLimit === undefined
      ? ""
      : isArabic
        ? `تنبيه : يرجى اختيار ${entityLabel} من المستوى ${nodeLevelLimit}`
        : `Notice: Please select the ${isAccountCenter ? "cost center" : "account"} from level ${nodeLevelLimit}`;

  const selectedKey = String(selectedValue || "");

  const normalizedData = useMemo(
    () => normalizeNodes(data, isArabic),
    [data, isArabic]
  );

  const allowedTypesSet = useMemo(
    () =>
      new Set(
        (Array.isArray(allowedAccountTypes) ? allowedAccountTypes : [])
          .map((type) => normalizeAccountType(type))
          .filter(Boolean)
      ),
    [allowedAccountTypes]
  );
  const blockedAccountsSet = useMemo(
    () =>
      new Set(
        (Array.isArray(blockedAccountNumbers) ? blockedAccountNumbers : [])
          .map((number) => String(number || "").trim())
          .filter(Boolean)
      ),
    [blockedAccountNumbers]
  );
  const isBlockedNode = useCallback(
    (node) => {
      if (blockedAccountsSet.size === 0) return false;
      const accountNumber = String(node?.accountNumber || "").trim();
      return blockedAccountsSet.has(accountNumber);
    },
    [blockedAccountsSet]
  );
  const isSelectableNode = useCallback(
    (node) => {
      if (isBlockedNode(node)) return false;
      
      // Check level limit - use actualLevel if available, otherwise use node.level
      const checkLevel = node.actualLevel !== undefined ? node.actualLevel : node.level;
      
      if (nodeLevelLimit !== null && nodeLevelLimit !== undefined && checkLevel !== undefined) {
        if (Number(checkLevel) !== Number(nodeLevelLimit)) {
          return false;
        }
      }
      
      return (
        allowedTypesSet.size === 0 ||
        allowedTypesSet.has(resolveNodeType(node?.raw))
      );
    },
    [allowedTypesSet, isBlockedNode, nodeLevelLimit]
  );

  const flatNodes = useMemo(
    () => flattenNodes(normalizedData, []),
    [normalizedData]
  );

  const filteredNodes = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();

    if (!query) return [];

    return flatNodes.filter((node) => {
      if (isBlockedNode(node)) return false;
      // Only allow nodes at exact maximum level
      if (nodeLevelLimit !== null && nodeLevelLimit !== undefined && node.level !== undefined) {
        if (Number(node.level) !== Number(nodeLevelLimit)) {
          return false;
        }
      }
      const text = `${node.accountNumber} ${node.accountName}`.toLowerCase();
      return text.includes(query);
    });
  }, [flatNodes, searchTerm, isBlockedNode, nodeLevelLimit]);

  useEffect(() => {
    if (!isOpen) return;

    const defaults = {};

    // Expand only nodes whose children level is less than nodeLevelLimit
    const expandNodes = (nodes, currentLevel = 0) => {
      nodes.forEach((node) => {
        const nextLevel = currentLevel + 1;
        
        // Open nodes only if their children will be below the target level
        // or if they have the target level (to show their children which are the selectable ones)
        if (
          nodeLevelLimit === null ||
          nodeLevelLimit === undefined ||
          nextLevel < nodeLevelLimit
        ) {
          defaults[node.key] = true;
          if (node.children.length > 0) {
            expandNodes(node.children, nextLevel);
          }
        }
      });
    };

    expandNodes(normalizedData);

    setExpanded(defaults);

    setSearchTerm("");
  }, [isOpen, normalizedData, nodeLevelLimit]);

  if (!isOpen) return null;

  const handlePick = (node) => {
    if (!isSelectableNode(node)) return;

    onSelect?.({
      id: node.id,
      account_no: node.accountNumber,
      name: node.accountName,
      raw: node.raw,
    });

    onClose?.();
  };

  const toggleNode = (key) => {
    setExpanded((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const renderTree = (nodes, level = 0) => {
    return nodes.map((node) => {
      if (isBlockedNode(node)) return null;
      const isSelected =
        selectedKey === node.accountNumber || selectedKey === node.id;

      const isExpanded = !!expanded[node.key];

      // Use actual render level + 1 for level comparison
      const actualLevel = level + 1;
      const nodeWithLevel = { ...node, actualLevel };
      const canSelect = isSelectableNode(nodeWithLevel);

      return (
        <div key={node.key}>
          <TreeRow
            node={node}
            level={level}
            isExpanded={isExpanded}
            isSelected={isSelected}
            canSelect={canSelect}
            onToggle={toggleNode}
            onSelect={handlePick}
            isArabic={isArabic}
            selectLabel={selectLabel}
          />

          {node.children.length > 0 &&
            !isExpanded &&
            renderTree(node.children, level + 1)}
        </div>
      );
    });
  };

  return (
    <div
      className="fixed inset-0 z-[80] bg-[#261759]/35 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-5xl max-h-[86vh] bg-slate-100 rounded-lg border border-slate-300 shadow-xl overflow-hidden flex flex-col"
        dir={isArabic ? "rtl" : "ltr"}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b bg-white">
          <h3 className="text-[#261759] font-[800] text-lg">
            {title || t("accounts.accountsGuide")}
          </h3>

          <button
            type="button"
            className="w-8 h-8 rounded-md border border-slate-300 flex items-center justify-center text-slate-600 hover:bg-slate-100"
            onClick={onClose}
          >
            <X size={16} />
          </button>
        </div>

        <div className="p-4 border-b bg-slate-50">
          <div className="relative">
            <Search
              size={16}
              className={`absolute top-1/2 -translate-y-1/2 text-[#261759] ${
                isArabic ? "right-3" : "left-3"
              }`}
            />

            <input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t("common.search")}
              className={`h-11 w-full rounded-md border border-[#8D82B6] border-opacity-55 bg-white text-[#261759] text-sm font-[700] outline-none ${
                isArabic ? "pr-10 pl-3" : "pl-10 pr-3"
              }`}
            />
          </div>
        </div>

        <div className="flex-1 overflow-auto p-3 bg-[#f3f4f6]">
          {searchTerm.trim() ? (
            <div className="space-y-2">
              {filteredNodes.length > 0 ? (
                filteredNodes.map((node) => {
                  const isSelected =
                    selectedKey === node.accountNumber ||
                    selectedKey === node.id;

                  const canSelect = isSelectableNode(node);

                  return (
                    <SearchResultRow
                      key={`search-${node.key}`}
                      node={node}
                      isSelected={isSelected}
                      canSelect={canSelect}
                      onSelect={handlePick}
                      isArabic={isArabic}
                      selectLabel={selectLabel}
                    />
                  );
                })
              ) : (
                <div className="text-center text-sm text-[#261759] py-8">
                  {t("common.noData")}
                </div>
              )}
            </div>
          ) : (
            <div className="bg-white rounded-lg border border-slate-200 p-2">
              {renderTree(normalizedData)}
            </div>
          )}
        </div>

        <div className="flex items-center gap-3 border-t bg-white px-4 py-3">
          {levelNotice && (
            <div className="flex items-center gap-2 text-xs font-[700] text-yellow-800">
              <AlertTriangle size={16} className="shrink-0 text-yellow-800" />
              <span>{levelNotice}</span>
            </div>
          )}
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-md border border-slate-300 text-[#261759] font-[700] hover:bg-slate-100"
            style={{ marginInlineStart: "auto" }}
          >
            {t("common.cancel")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TreePopup;
